import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { useGeminiChat } from './hooks/useGeminiChat';
import WaterIcon from './assets/WaterIcon';
import './App.css';

// --- SVG Icon Components ---
const FoodIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11 9H9V2H7V9H5V2H3V9C3 11.21 4.79 13 7 13V22H9V13C11.21 13 13 11.21 13 9V2H11V9ZM16 6V14H18.5C20.43 14 22 12.43 22 10.5C22 8.57 20.43 7 18.5 7H16V6H21V4H16V2H21V0H16C13.79 0 12 1.79 12 4V9C12 11.21 13.79 13 16 13H18.5C21.54 13 24 10.78 24 8C24 5.22 21.54 3 18.5 3H16V2H12V4H16V6Z" fill="#888" />
  </svg>
);
const SleepIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.29,2.4C5.49,3.44 3,6.83 3,11c0,4.97,4.03,9,9,9c3.14,0,5.89-1.6,7.48-4.01c-1.1,0.09-2.22,0.14-3.36,0.14 c-4.97,0-9-4.03-9-9C7.11,6.03,7.91,4.94,9.29,2.4z" fill="#888" />
  </svg>
);
const SuggestIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1zm3-19C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7z" fill="#888" />
  </svg>
);
const WorkoutIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20.57 14.86L22 13.43L20.57 12 17 15.57L8.43 7 12 3.43 10.57 2 9.14 3.43 7.71 2 5.57 4.14 4.14 2.71 2.71 4.14l1.43 1.43L2 7.71l1.43 1.43L2 10.57 3.43 12 7 8.43 15.57 17 12 20.57 13.43 22l1.43-1.43L16.29 22l2.14-2.14 1.43 1.43 1.43-1.43-1.43-1.43L22 16.29z" fill="#888" />
  </svg>
);
const ProgressIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3.5 18.49L9.5 12.49L13.5 16.49L21.5 6.99V12.5H23.5V4.5H15.5V6.5H20.01L13.5 14.49L9.5 10.49L1.5 18.49L3.5 20.49Z" fill="#888" />
  </svg>
);
const SendPlaneIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z" fill="white" />
  </svg>
);


// --- UI Components ---

const Header = ({ activeTab, setActiveTab, onVoiceModeClick }) => {
  const handleChatClick = () => {
    // If voice mode is open, this button should close it.
    // We'll assume the parent component handles the state logic.
    setActiveTab('chat');
  }

  return (
    <header className="app-header">
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'chat' ? 'active' : ''}`}
          onClick={handleChatClick}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2Z"></path></svg>
          Chat
        </button>
        <button
          className={`tab ${activeTab === 'voice' ? 'active' : ''}`}
          onClick={onVoiceModeClick}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 14C13.66 14 15 12.66 15 11V5C15 3.34 13.66 2 12 2C10.34 2 9 3.34 9 5V11C9 12.66 10.34 14 12 14ZM10.8 4.9C10.8 4.4 11.35 4 12 4C12.65 4 13.2 4.4 13.2 4.9V11.1C13.2 11.6 12.65 12 12 12C11.35 12 10.8 11.6 10.8 11.1V4.9ZM17 11C17 13.76 14.76 16 12 16C9.24 16 7 13.76 7 11H5C5 14.53 7.72 17.43 11 17.92V21H13V17.92C16.28 17.43 19 14.53 19 11H17Z"></path></svg>
          Voice
        </button>
      </div>
    </header>
  );
};

const BotAvatar = () => (
  <div className="avatar bot-avatar">
    <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="botGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#1FBF8F', stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: '#1AA7B8', stopOpacity: 1 }} />
        </linearGradient>
        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="1" dy="1" stdDeviation="1" floodColor="#000000" floodOpacity="0.2" />
        </filter>
      </defs>
      <path d="M27 16.5C27 19.5376 27 21.0563 26.4142 22.2782C25.899 23.346 25.0627 24.1823 24 24.6975C22.7782 25.2833 21.2594 25.2833 18.2218 25.2833H13.7782C10.7406 25.2833 9.22183 25.2833 8 24.6975C6.93733 24.1823 6.10098 23.346 5.58579 22.2782C5 21.0563 5 19.5376 5 16.5V15.5C5 12.4624 5 10.9437 5.58579 9.72183C6.10098 8.65398 6.93733 7.81763 8 7.30245C9.22183 6.71665 10.7406 6.71665 13.7782 6.71665H18.2218C21.2594 6.71665 22.7782 6.71665 24 7.30245C25.0627 7.81763 25.899 8.65398 26.4142 9.72183C27 10.9437 27 12.4624 27 15.5V16.5Z" fill="url(#botGradient)" filter="url(#shadow)" />
      <path d="M12.5 15C12.5 15.8284 11.8284 16.5 11 16.5C10.1716 16.5 9.5 15.8284 9.5 15C9.5 14.1716 10.1716 13.5 11 13.5C11.8284 13.5 12.5 14.1716 12.5 15Z" fill="white" />
      <path d="M22.5 15C22.5 15.8284 21.8284 16.5 21 16.5C20.1716 16.5 19.5 15.8284 19.5 15C19.5 14.1716 20.1716 13.5 21 13.5C21.8284 13.5 22.5 14.1716 22.5 15Z" fill="white" />
      <path d="M16 21C18.2091 21 20 19.2091 20 17H12C12 19.2091 13.7909 21 16 21Z" fill="white" />
    </svg>
  </div>
);

const UserAvatar = () => (
  <div className="avatar user-avatar">
    <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="userGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#1FBF8F', stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: '#1AA7B8', stopOpacity: 1 }} />
        </linearGradient>
      </defs>
      <path d="M27 16.5C27 19.5376 27 21.0563 26.4142 22.2782C25.899 23.346 25.0627 24.1823 24 24.6975C22.7782 25.2833 21.2594 25.2833 18.2218 25.2833H13.7782C10.7406 25.2833 9.22183 25.2833 8 24.6975C6.93733 24.1823 6.10098 23.346 5.58579 22.2782C5 21.0563 5 19.5376 5 16.5V15.5C5 12.4624 5 10.9437 5.58579 9.72183C6.10098 8.65398 6.93733 7.81763 8 7.30245C9.22183 6.71665 10.7406 6.71665 13.7782 6.71665H18.2218C21.2594 6.71665 22.7782 6.71665 24 7.30245C25.0627 7.81763 25.899 8.65398 26.4142 9.72183C27 10.9437 27 12.4624 27 15.5V16.5Z" fill="url(#userGradient)" filter="url(#shadow)" />
      <path d="M16 17C18.2091 17 20 15.2091 20 13C20 10.7909 18.2091 9 16 9C13.7909 9 12 10.7909 12 13C12 15.2091 13.7909 17 16 17Z" fill="white" />
      <path d="M23 24C23 20.134 19.866 17 16 17C12.134 17 9 20.134 9 24" stroke="white" stroke-width="2" stroke-linecap="round" />
    </svg>
  </div>
);

const MessageBubble = ({ msg }) => {
  return (
    <div className={`message-container ${msg.sender}`}>
      {msg.sender === 'bot' ? <BotAvatar /> : <UserAvatar />}
      <div className={`message-bubble ${msg.sender}`}>
        {msg.isLoading ? <TypingIndicator /> : <ReactMarkdown>{msg.text}</ReactMarkdown>}
      </div>
    </div>
  );
};



const TypingIndicator = () => (
  <div className="typing-indicator">
    <span></span>
    <span></span>
    <span></span>
  </div>
);

const QuickReplies = ({ onQuickReply }) => {
  const actions = [
    { title: "Log water", icon: <WaterIcon size={20} color="#888" />, payload: "Log water" },
    { title: "Log my food", icon: <FoodIcon />, payload: "Log my food" },
    { title: "Log my sleep", icon: <SleepIcon />, payload: "Log my sleep" },
    { title: "Suggest a healthy meal", icon: <SuggestIcon />, payload: "Suggest a healthy meal" },
    { title: "Plan my workout", icon: <WorkoutIcon />, payload: "Plan my workout" },
    { title: "Today's progress", icon: <ProgressIcon />, payload: "How is my progress today?" },
  ];

  return (
    <div className="quick-actions-section">
      <h2>Quick Actions</h2>
      <div className="quick-replies-container">
        {actions.map((reply, index) => (
          <button key={index} onClick={() => onQuickReply(reply.payload)} className="quick-reply-button">
            {reply.icon}
            <span>{reply.title}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

const MessageInput = ({ userInput, setUserInput, handleSendMessage, isLoading, onVoiceModeClick }) => {
  return (
    <footer className="app-footer">
      <form onSubmit={handleSendMessage} className="message-form">
        <input
          type="text"
          value={userInput}
          onChange={(e) => setUserInput(e.target.value)}
          placeholder="Ask about your health..."
          disabled={isLoading}
        />
        <button type="submit" disabled={isLoading || !userInput.trim()} className="send-button">
          {isLoading ? <div className="spinner"></div> : <SendPlaneIcon />}
        </button>
      </form>
    </footer>
  );
};

// --- New Component for the Voice App Overlay ---
const VoiceModeOverlay = ({ isOpen, onClose, url, activeTab, setActiveTab }) => {
  if (!isOpen) {
    return null;
  }

  return (
    <div className="voice-overlay">
      <Header
        activeTab={activeTab}
        setActiveTab={(tab) => {
          setActiveTab(tab);
          if (tab === 'chat') {
            onClose(); // Close overlay when chat is clicked
          }
        }}
        onVoiceModeClick={() => { }} // Voice is already active
      />
      <div className="voice-overlay-content">
        <iframe
          src={url}
          title="GOQii AI Coach Voice Mode"
          allow="microphone; camera"
          style={{ width: '100%', height: '100%', border: 'none' }}
        />
      </div>
    </div>
  );
};

// --- Main App Component ---
export default function App() {
  const { messages, isLoading, userDetails, sendMessage, isReady } = useGeminiChat();
  const [userInput, setUserInput] = useState('');
  const [isVoiceModeOpen, setIsVoiceModeOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('chat');
  const messagesEndRef = useRef(null);
  const [showQuickReplies, setShowQuickReplies] = useState(false);

  useEffect(() => {
    if (!showQuickReplies && isReady && !messages.some(msg => msg.isLoading)) {
      setShowQuickReplies(true);
    }
  }, [isReady, messages, showQuickReplies]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const theme = urlParams.get('theme');
    if (theme === 'dark') {
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.setAttribute('data-theme', 'light');
    }
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  const handleVoiceModeToggle = () => {
    if (userDetails && userDetails.goqiiUserId) {
      setActiveTab('voice');
      setIsVoiceModeOpen(true);
    } else {
      console.error("Cannot open voice mode: userDetails or goqiiUserId is missing.");
      alert("Could not start voice mode. User information is not available.");
    }
  };

  const handleCloseVoiceMode = () => {
    setIsVoiceModeOpen(false);
    setActiveTab('chat');
  };

  const voiceAppUrl = useMemo(() => {
    if (userDetails && userDetails.goqiiUserId) {
      let themeParam = '';
      if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        const theme = urlParams.get('theme');
        if (theme) {
          themeParam = `&theme=${theme}`;
        }
      }
      return `https://aivoicegoqiicoach.goqii.com/?goqiiUserId=${userDetails.goqiiUserId}${themeParam}`;
    }
    return null;
  }, [userDetails]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!userInput.trim()) return;
    sendMessage(userInput);
    setUserInput('');
  };

  const handleQuickReply = (payload) => {
    if (isLoading || !isReady) return;
    sendMessage(payload);
  };

  return (
    <>
      <VoiceModeOverlay
        isOpen={isVoiceModeOpen}
        onClose={handleCloseVoiceMode}
        url={voiceAppUrl}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
      <div className="app-container" style={{ display: isVoiceModeOpen ? 'none' : 'flex' }}>
        <Header
          activeTab={activeTab}
          setActiveTab={(tab) => {
            setActiveTab(tab);
            if (tab === 'voice') {
              handleVoiceModeToggle();
            }
          }}
          onVoiceModeClick={handleVoiceModeToggle}
        />
        {showQuickReplies && (
          <QuickReplies onQuickReply={handleQuickReply} />
        )}
        <main className="chat-window">
          {messages.map((msg, index) => (
            <MessageBubble key={index} msg={msg} userDetails={userDetails} />
          ))}
          <div ref={messagesEndRef} />
        </main>
        <MessageInput
          userInput={userInput}
          setUserInput={setUserInput}
          handleSendMessage={handleSendMessage}
          isLoading={isLoading}
        />
      </div>
    </>
  );
}
